import { useState, useEffect } from 'react';
import axios from 'axios';
import MainLayout from '../components/MainLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CaseFiling = ({ user, onLogout }) => {
  const [formData, setFormData] = useState({
    claimant_id: '',
    ar_id: '',
    batch_name: ''
  });
  const [file, setFile] = useState(null);
  const [claimants, setClaimants] = useState([]);
  const [ars, setArs] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchClaimants();
  }, []);

  useEffect(() => {
    if (formData.claimant_id) {
      fetchARs(formData.claimant_id);
    }
  }, [formData.claimant_id]);

  const fetchClaimants = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/admin/claimants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClaimants(res.data.claimants);
    } catch (err) {
      console.error('Failed to fetch claimants:', err);
    }
  };

  const fetchARs = async (claimantId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API_URL}/admin/authorized-representatives?claimant_id=${claimantId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setArs(res.data.authorized_representatives);
    } catch (err) {
      console.error('Failed to fetch ARs:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setDuplicates([]);
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const formDataObj = new FormData();
      formDataObj.append('claimant_id', formData.claimant_id);
      formDataObj.append('ar_id', formData.ar_id);
      formDataObj.append('batch_name', formData.batch_name);
      formDataObj.append('file', file);

      const res = await axios.post(`${API_URL}/cases/upload-csv`, formDataObj, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (res.data.require_confirmation) {
        setDuplicates(res.data.duplicates);
        setError(`Warning: ${res.data.duplicates.length} duplicate agreement numbers found`);
      } else {
        setMessage(res.data.message);
        setFormData({ claimant_id: '', ar_id: '', batch_name: '' });
        setFile(null);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload cases');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmWithDuplicates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/cases/upload-csv/confirm`,
        { ...formData, duplicates },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage('Cases uploaded successfully (including duplicates)');
      setDuplicates([]);
      setFormData({ claimant_id: '', ar_id: '', batch_name: '' });
      setFile(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to confirm upload');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout user={user} onLogout={onLogout}>
      <div className="page-header">
        <h1 className="page-title">Case Filing</h1>
        <p className="page-description">Upload cases via CSV file</p>
      </div>

        <div className="card">
          {message && <div className="success-message">{message}</div>}
          {error && <div className="error-message">{error}</div>}

          {duplicates.length > 0 && (
            <div style={{ background: '#fff3cd', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
              <h3 style={{ margin: '0 0 0.5rem 0' }}>Duplicate Agreement Numbers:</h3>
              <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                {duplicates.map((dup, idx) => (
                  <li key={idx}>{dup}</li>
                ))}
              </ul>
              <button
                data-testid="confirm-duplicates-button"
                className="btn-primary"
                onClick={handleConfirmWithDuplicates}
                disabled={loading}
              >
                Proceed Anyway
              </button>
              <button
                className="btn-secondary"
                style={{ marginLeft: '0.5rem' }}
                onClick={() => setDuplicates([])}
              >
                Cancel
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Select Claimant</label>
              <select
                data-testid="claimant-select"
                className="form-select"
                value={formData.claimant_id}
                onChange={(e) => setFormData({ ...formData, claimant_id: e.target.value, ar_id: '' })}
                required
              >
                <option value="">Select a claimant</option>
                {claimants.map((c) => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
            </div>

            {formData.claimant_id && (
              <div className="form-group">
                <label>Select Authorized Representative</label>
                <select
                  data-testid="ar-select"
                  className="form-select"
                  value={formData.ar_id}
                  onChange={(e) => setFormData({ ...formData, ar_id: e.target.value })}
                  required
                >
                  <option value="">Select an AR</option>
                  {ars.map((ar) => (
                    <option key={ar._id} value={ar._id}>{ar.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Batch Name</label>
              <input
                data-testid="batch-name-input"
                className="form-input"
                value={formData.batch_name}
                onChange={(e) => setFormData({ ...formData, batch_name: e.target.value })}
                placeholder="e.g., Batch 2024-01"
                required
              />
            </div>

            <div className="form-group">
              <label>Upload CSV File</label>
              <input
                data-testid="csv-file-input"
                type="file"
                className="form-input"
                accept=".csv"
                onChange={(e) => setFile(e.target.files[0])}
                required
              />
              <small style={{ color: '#718096', display: 'block', marginTop: '0.5rem' }}>
                CSV should contain: agreementNo, and other case details
              </small>
            </div>

            <button
              data-testid="upload-csv-button"
              type="submit"
              className="btn-primary"
              disabled={loading || duplicates.length > 0}
            >
              {loading ? 'Uploading...' : 'Upload Cases'}
            </button>
          </form>
        </div>
    </MainLayout>
  );
};

export default CaseFiling;
